import { z } from 'zod';
import { aiCategory, feedLayout } from './topics.js';
import { uiLanguage, premiumStatus } from './i18n.js';

export const quietHours = z.object({
  from: z.string().regex(/^\d{2}:\d{2}$/),
  to: z.string().regex(/^\d{2}:\d{2}$/),
});
export type QuietHours = z.infer<typeof quietHours>;

// Bare field validators (no defaults). Zod 4 keeps `.default()` through `.partial()`,
// so a patch schema derived from the defaulted schema would silently reset every
// omitted key — the patch must be built from these instead.
const settingsFields = {
  notificationsEnabled: z.boolean(),
  quietHours: quietHours.nullable(),
  timezone: z.string(),
  language: uiLanguage,
  theme: z.enum(['auto', 'light', 'dark']),
  markReadOnOpen: z.boolean(),
  fontSize: z.enum(['sm', 'md', 'lg']),
  // how the main feed is laid out (see docs/superpowers/specs/2026-09-08-feed-layouts-design.md)
  feedLayout,
  // topic sections the user hid in the topics layout (presentation only, server returns all)
  hiddenTopics: z.array(aiCategory),
  // premium: translate foreign-language cards/articles into `language`
  translateContent: z.boolean(),
};

export const settingsSchema = z.object({
  notificationsEnabled: settingsFields.notificationsEnabled.default(true),
  quietHours: settingsFields.quietHours.default({ from: '22:00', to: '07:00' }),
  timezone: settingsFields.timezone.default('Europe/Prague'),
  language: settingsFields.language.default('cs'),
  theme: settingsFields.theme.default('auto'),
  markReadOnOpen: settingsFields.markReadOnOpen.default(true),
  fontSize: settingsFields.fontSize.default('md'),
  feedLayout: settingsFields.feedLayout.default('reels'),
  hiddenTopics: settingsFields.hiddenTopics.default([]),
  translateContent: settingsFields.translateContent.default(true),
});
export type Settings = z.infer<typeof settingsSchema>;

export const settingsPatch = z.object(settingsFields).partial();
export type SettingsPatch = z.infer<typeof settingsPatch>;

/** GET/PATCH /api/settings body: settings plus the premium status (read-only). */
export const settingsResponse = settingsSchema.extend({ premium: premiumStatus });
export type SettingsResponse = z.infer<typeof settingsResponse>;

export const defaultSettings: Settings = settingsSchema.parse({});
